import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faBox, faChevronRight, faSpinner } from "@fortawesome/free-solid-svg-icons";
import AccountSidebar from "../../components/AccountSidebar/AccountSidebar";
import { getOrdersApi } from "../../services/api.services";
import { language } from "../../languageProvider/language/language";
import { defaultLanguage } from "../../helper/utility";
import "../Profile.page.css";

const STATUS_COLORS = {
  0: "#ffc107",  // Da lavorare
  1: "#17a2b8",  // In lavorazione
  2: "#28a745",  // Completato
  3: "#dc3545",  // Annullato
};

function OrdersPage() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAllOrders = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let allOrders = [];
      let page = 1;
      let totalPages = 1;

      // Carica tutte le pagine
      while (page <= totalPages) {
        const res = await getOrdersApi(page, 10);
        if (res.status === 200 && res.data) {
          allOrders = [...allOrders, ...(res.data.orders || [])];
          totalPages = res.data.pagination?.total_pages || 1;
          page++;
        } else {
          setError(res.message || "Errore nel caricamento degli ordini");
          break;
        }
      }

      // Ordina dal più recente al meno recente
      allOrders.sort((a, b) => new Date(b.request_datetime) - new Date(a.request_datetime));
      setOrders(allOrders);
    } catch (err) {
      setError("Errore nel caricamento degli ordini");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAllOrders();
  }, [fetchAllOrders]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="up-page">
      <div className="up-topbar">
        <div className="up-topbar__left">
          <div className="up-topbar__back" onClick={() => navigate("/leaflet")}>
            <FontAwesomeIcon icon={faArrowLeft} />
          </div>
          <h1 className="up-topbar__title">{language[defaultLanguage].ordini}</h1>
        </div>
      </div>

      <div className="up-layout">
        <AccountSidebar />
        <div className="up-main">
          {loading && (
            <div className="up-card" style={{ textAlign: "center", padding: "40px" }}>
              <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: "1.5rem", color: "#999" }} />
            </div>
          )}

          {error && (
            <div className="up-card" style={{ textAlign: "center", color: "#dc3545" }}>
              <p>{error}</p>
              <button className="up-btn up-btn--outline" onClick={fetchAllOrders}>
                Riprova
              </button>
            </div>
          )}

          {!loading && !error && orders.length === 0 && (
            <div className="up-card" style={{ textAlign: "center", padding: "40px", color: "#999" }}>
              <FontAwesomeIcon icon={faBox} style={{ fontSize: "2rem", marginBottom: "10px", color: "#ddd" }} />
              <p>Nessun ordine trovato</p>
            </div>
          )}

          {orders.map((order) => {
            const color = STATUS_COLORS[order.status] || "#999";
            return (
              <div className="up-card" key={order.id} style={{ cursor: "pointer" }} onClick={() => navigate(`/profilo/ordini/${order.id}`)}>
                {/* Riga 1: numero + data, chevron a destra */}
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "12px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "10px", minWidth: 0 }}>
                    <FontAwesomeIcon icon={faBox} className="up-card__icon" />
                    <div style={{ minWidth: 0 }}>
                      <h2 className="up-card__title" style={{ marginBottom: "2px" }}>
                        #{order.numero_ordine}
                      </h2>
                      <span style={{ fontSize: "0.82rem", color: "#999" }}>
                        {formatDate(order.request_datetime)}
                      </span>
                    </div>
                  </div>
                  <FontAwesomeIcon
                    icon={faChevronRight}
                    style={{ color: "#ccc", fontSize: "0.8rem", marginTop: "5px", flexShrink: 0 }}
                  />
                </div>

                {/* Riga 2: badge stato */}
                <div style={{ marginTop: "10px" }}>
                  <span style={{
                    fontSize: "0.78rem",
                    fontWeight: 600,
                    color: color,
                    backgroundColor: color + "18",
                    padding: "3px 10px",
                    borderRadius: "12px",
                    display: "inline-block",
                  }}>
                    {order.status_label}
                  </span>
                </div>

                {/* Riga 3: metodo consegna a sinistra, totale prominente a destra */}
                <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: "12px", marginTop: "10px" }}>
                  <span style={{ fontSize: "0.82rem", color: "#999" }}>
                    {order.delivery_method_label}
                  </span>
                  <span style={{ fontSize: "1.05rem", fontWeight: 700, color: "#222", whiteSpace: "nowrap" }}>
                    {order.total_formatted || `€ ${order.total}`}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default OrdersPage;
